import { useQuery } from "@tanstack/react-query";
import type { QueryKey } from "@tanstack/react-query";
import { RetryRunDialog as DesignRetryRunDialog } from "@studio-features/meta-crew/retry-run";
import type { RetryRunView, RetrySettings } from "@studio-features/meta-crew/retry-run-data";
import { api, errorMessage } from "../lib/api";
import { useAiModels } from "../lib/llm-api";
import { useAction, useDevices, useMachines } from "../lib/queries";
import type { RunDetail, TaskDetail } from "../lib/types";

/**
 * 설정 바꿔 재시도 (관리자). 2026-10-21 사용자: 실패 뒤 모델·설정을 바꿔 다시 돌리고, 구조상 못 바꾸는 건 막아 둔다.
 * 화면은 시안 RetryRunDialog (요청서 27절, 66ecd3a). 여기서는 retry-settings·모델·머신·폰을 시안 view 로 맞춘다.
 * 바꿀 수 있는지는 서버 settings.editable/reason 만 따르고, 실행 상세 대신 열린다 (다이얼로그는 한 단).
 */
export function RetryRunDialog({
  base,
  run,
  task,
  customerId,
  customerName,
  typeName,
  invalidate,
  onClose,
  onDone,
}: {
  /** /c/:customerId/runs/:runId 또는 /admin/runs/:runId */
  base: string;
  run: RunDetail;
  task: TaskDetail | null | undefined;
  customerId: string;
  customerName: string;
  typeName: string;
  invalidate: QueryKey[];
  onClose: () => void;
  onDone: () => void;
}) {
  const settings = useQuery({ queryKey: ["retry-settings", base], queryFn: () => api.get<RetrySettings>(`${base}/retry-settings`) });
  const models = useAiModels(true);
  const machines = useMachines(customerId);
  const devices = useDevices(customerId);
  const retry = useAction((body: Record<string, string | boolean | null>) => api.post(`${base}/retry`, body), invalidate);

  const params = task?.params ?? {};
  const str = (v: unknown) => (typeof v === "string" && v ? v : null);
  const body = str(params.body) ?? str(params.manuscript) ?? run.manuscript ?? null;
  const target = task?.targetCustomer ?? (run.targetCustomerId ? { id: run.targetCustomerId, name: run.targetCustomerName ?? run.targetCustomerId } : null);
  const context: RetryRunView["context"] = {
    runId: run.id,
    title: task?.title ?? run.jobName ?? typeName,
    execution: { id: customerId, name: customerName },
    target: target ?? { id: customerId, name: customerName },
    typeName,
    manuscript: body ? { title: str(params.title) ?? task?.title ?? "원고", body } : null,
    attachments: ((task?.attachments ?? []) as { fileId?: string; name?: string }[])
      .filter((a) => a.fileId && a.name)
      .map((a) => ({ fileId: a.fileId!, name: a.name! })),
  };
  // 배정 가능한 PC = 페어링됐고 폐기되지 않은 머신 (작업 등록의 "PC 없음" 경고와 같은 기준)
  const machineItems = (machines.data?.items ?? []).filter((m) => m.pairedAt && m.status !== "retired");
  const loading = settings.isLoading || machines.isLoading || devices.isLoading;
  const failure = settings.error ?? machines.error ?? devices.error;
  const view: RetryRunView = loading
    ? { context, canRetry: true, status: "loading" }
    : failure || !settings.data
      ? { context, canRetry: true, status: "error", error: errorMessage(failure) }
      : {
          context,
          canRetry: true,
          status: "ready",
          settings: settings.data,
          models: models.data,
          connectedMachineCount: machineItems.length,
          machines: machineItems.map((m) => ({ id: m.id, label: m.label, connected: m.connected })),
          devices: (devices.data?.items ?? [])
            .filter((d) => d.machineId)
            .map((d) => ({
              serial: d.serial,
              label: d.alias ?? d.model ?? d.serial,
              machineId: d.machineId!,
              machineLabel: d.machineLabel ?? machineItems.find((m) => m.id === d.machineId)?.label ?? "미연결",
            })),
        };
  return (
    <DesignRetryRunDialog
      open
      view={view}
      onClose={onClose}
      onRetry={async (patch) => {
        try {
          await retry.mutateAsync(patch);
        } catch (e) {
          throw new Error(errorMessage(e));
        }
      }}
      onDone={onDone}
      onReload={() => {
        void settings.refetch();
        void machines.refetch();
        void devices.refetch();
      }}
    />
  );
}
